import { Component, EventEmitter, Inject, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { JwtLegFiClaims } from '../../../../services/auth/jwt-legfi-claims.model';
import { LobBankAccount } from '../../../../models/entities/lob-bank-account';
import { BillPayService } from '../../../../services/members/bill-pay.service';
import { LegFiJwtService } from '../../../../services/auth/legfi-jwt.service';
import { OrganizationService } from '../../../../services/organization/organization.service';
import moment from 'moment-timezone';
import { MembersService } from '../../../../services/members/members.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { environment } from '../../../../../environments/environment';
import { Organization } from '../../../../models/entities/organization';
import { GrowlerService } from '../../../../services/growler.service';
import { RecaptchaComponent } from 'ng-recaptcha';
import { MatDialogRef } from '@angular/material/dialog';

export interface AddBillPayBankAccountOutput
{
    account: LobBankAccount;
    success: boolean;
}

@UntilDestroy()
@Component({
    selector: 'lf-add-bill-pay-account',
    styleUrls: ['./add-bill-pay-account.scss'],
    templateUrl: './add-bill-pay-account.component.html',
})
export class AddBillPayAccountComponent implements OnInit
{
    // form and captcha
    @ViewChild('recaptchaComponent') recaptchaComponent: RecaptchaComponent;
    siteKey = environment.RecaptchaSiteKey;
    captchaToken: string = null;
    model: FormGroup = this._builder.group({
        description: [
            '',
            Validators.required,
        ],
        routingNumber: [
            '',
            Validators.required,
        ],
        accountNumber: [
            '',
            Validators.required,
        ],
        signatory: [
            '',
            Validators.required,
        ],
    });
    errorMessage = 'This field is required.';

    // ids
    organizationId: number = null;
    membershipId: number = null;

    // state
    isTermsAgreedTo = false;
    isLoggedIn = false;
    isMemberDataLoaded = false;
    isAgreeInFlight = false;
    isSubmitInFlight = false;
    isPreferencesLoaded = false;
    isManualAddEnabled = false;

    jwt: JwtLegFiClaims;

    constructor(
            private _billPayService: BillPayService,
            private _builder: FormBuilder,
            private _dialogRef: MatDialogRef<AddBillPayAccountComponent>,
            private _membersService: MembersService,
            private _organizationService: OrganizationService,
            private _growlerService: GrowlerService,
    ) {
    }

    ngOnInit() {
        this.jwt = LegFiJwtService.read();

        this.getOrgPreferences();
        this.getMember();
    }

    captchaResolved($event) {
        this.captchaToken = $event;
    }

    getOrgPreferences() {
        this._organizationService
                .getOrganizationById(null, ['preferences'])
                .subscribe(
                        (org: Organization) => {
                            this.isPreferencesLoaded = true;
                            this.isManualAddEnabled = org.preferences.billPayBankAccountAddingEnabled;
                        },
                );
    }

    getMember() {
        this._membersService
                .getMemberProfileFields(this.jwt.orgId, this.jwt.id, [
                    'membership_id',
                    'agreed_to_billpay_terms',
                ])
                .pipe(untilDestroyed(this))
                .subscribe(
                        // Success
                        (member: any) => {
                            this.membershipId = member.membershipId;

                            const isTermsAgreedToDate = member.agreedToBillpayTerms;
                            const timezone = LegFiJwtService.getTimezone();
                            const t = moment
                                    .utc(isTermsAgreedToDate, 'YYYY-MM-DD hh:mm:ss')
                                    .tz(timezone);
                            const reasonableDate = moment
                                    .utc('2017-01-01')
                                    .tz(timezone);

                            this.isTermsAgreedTo = t.isAfter(reasonableDate);

                            this.isMemberDataLoaded = true;
                        },
                        () => {
                            if (this.jwt.superUser) {
                                this.isTermsAgreedTo = true;
                                this.isMemberDataLoaded = true;
                            }
                        },
                );
    }

    saveTermAgreement() {
        const requestBody: { [key: string]: any } = {};
        requestBody['siteId'] = 1;
        requestBody['agreedToBillpayTerms'] = true;
        this.isAgreeInFlight = true;

        this._billPayService
                .patchMemberBillPayAgreement(
                        this.jwt.orgId,
                        this.jwt.id,
                        requestBody,
                )
                .subscribe(
                        () => {
                            this.isTermsAgreedTo = true;
                            this.isAgreeInFlight = false;
                        },
                        () => {
                            this.isAgreeInFlight = false;
                            this._growlerService.error(
                                    'Oops!',
                                    'There was a problem updating your agreement details.',
                            );
                        },
                );
    }

    cancelTermAgreement() {
        this._growlerService.error(
                'Warning',
                'You must agree to the Terms & Conditions before adding a bill pay bank account.',
        );

        this._dialogRef.close();
    }

    isFieldInvalid(fieldName: string): boolean {
        return this.model.get(fieldName).touched && !this.model.get(fieldName).value;
    }

    saveBankInfo() {
        if(this.isSubmitInFlight) {
            return;
        }

        this.model.markAllAsTouched();
        if (!this.model.valid) {
            this._growlerService.error(
                    'Error',
                    'Please check all required fields',
            );
            return;
        }

        if (this.captchaToken === null) {
            this._growlerService.error(
                    'Error',
                    'Please check the captcha box to proceed.',
            );
            return;
        }

        this.isSubmitInFlight = true;

        const requestBody: { [key: string]: any } = {};
        requestBody['description'] = this.model.get('description').value;
        requestBody['routingNumber'] = this.model.get('routingNumber').value;
        requestBody['accountNumber'] = this.model.get('accountNumber').value;
        requestBody['signatory'] = this.model.get('signatory').value;

        requestBody['captcha'] = this.captchaToken;

        this._billPayService
                .createLobBankAccount(this.membershipId, requestBody)
                .subscribe(
                        (bankAccount: LobBankAccount) => {
                            this._growlerService.success(
                                    'Success',
                                    'Your bank account has been added. Please follow instructions for confirming micro-deposits.',
                            );

                            this._dialogRef.close(<AddBillPayBankAccountOutput>{
                                account: bankAccount,
                                success: true,
                            });
                        },
                        err => {
                            this.recaptchaComponent.reset();

                            this._dialogRef.close();

                            this._growlerService.error('Failed Adding Bank Account',
                                    err['error']['errors']
                                            ? err['error']['errors'][0]['msg']
                                            : 'There was a problem adding your bank account.',
                            );
                        },
                );
    }

}
